import { useContext, useEffect, useState } from "react";

import TableContext from "context/table/tableContext";
import { NOT_FILTER_NAMES, ORDERS, SORT_RESOURCES } from "shared/types/enums";
import { QueryParams } from "shared/types/types";

import { getInitialCustomData, sortCustomDataRows } from "./functions";
import { Sorting } from "./types";

export const useSorting = (
    queryParams: QueryParams,
    setParams: (data: { params: QueryParams }) => void
) => {
    const {
        customData,
        idType,
        isInnerTable,
        rows,
        sortFromNewest,
        setTableData
    } = useContext(TableContext);

    const [sorting, setSorting] = useState<Sorting>({
        [NOT_FILTER_NAMES.Order]: ORDERS.Descending,
        [NOT_FILTER_NAMES.Sort]: ""
    });

    const { order, sort } = sorting;

    // Regular data sorting
    useEffect(() => {
        const sortingKeys = Object.keys(queryParams).filter(
            queryKey =>
                queryKey === NOT_FILTER_NAMES.Order ||
                queryKey === NOT_FILTER_NAMES.Sort
        );

        if (sortingKeys.length > 0) {
            const sortingParams = sortingKeys.reduce((previous, current) => {
                const singleObj = {
                    [current]: queryParams[current]
                };

                return { ...previous, ...singleObj };
            }, sorting);

            setSorting(sortingParams);
        } else {
            setSorting({
                [NOT_FILTER_NAMES.Order]: ORDERS.Descending,
                [NOT_FILTER_NAMES.Sort]: ""
            });
        }

        // eslint-disable-next-line
    }, [queryParams]);

    // Custom data sorting
    useEffect(() => {
        if (customData) {
            const initialCustomData = getInitialCustomData(
                sortFromNewest,
                customData,
                idType
            );

            const customDataRows = [...rows];

            sortCustomDataRows(customDataRows, sort, order);

            const sortedRows = sort === "" ? initialCustomData : customDataRows;

            setTableData({ rows: sortedRows });
        }

        // eslint-disable-next-line
    }, [customData, sort, order]);

    const getOrder = (resource: SORT_RESOURCES, isDesc: boolean) => {
        if (sort === resource) {
            return isDesc ? ORDERS.Ascending : ORDERS.Descending;
        }

        return ORDERS.Descending;
    };

    const getSort = (resource: SORT_RESOURCES, isDesc: boolean) => {
        if (!isDesc && sort === resource) {
            return "";
        }

        return resource;
    };

    const getUpdatedParamValue = (
        paramName: NOT_FILTER_NAMES.Sort | NOT_FILTER_NAMES.Order,
        newOrder: ORDERS,
        newSort: SORT_RESOURCES | ""
    ) => {
        const isNewSortEmpty = newSort === "";

        const isSortParam = paramName === NOT_FILTER_NAMES.Sort;

        if (isNewSortEmpty) {
            const defaultName = isSortParam
                ? SORT_RESOURCES.Id
                : ORDERS.Descending;

            return isInnerTable && sortFromNewest ? defaultName : "";
        }

        return isSortParam ? newSort : newOrder;
    };

    const sortColumn = (resource: SORT_RESOURCES) => {
        const isDesc = order === ORDERS.Descending;

        const newOrder = getOrder(resource, isDesc);
        const newSort = getSort(resource, isDesc);

        if (customData) {
            setSorting({
                [NOT_FILTER_NAMES.Order]: newOrder,
                [NOT_FILTER_NAMES.Sort]: newSort
            });
        } else {
            setParams({
                params: {
                    [NOT_FILTER_NAMES.Sort]: getUpdatedParamValue(
                        NOT_FILTER_NAMES.Sort,
                        newOrder,
                        newSort
                    ),
                    [NOT_FILTER_NAMES.Order]: getUpdatedParamValue(
                        NOT_FILTER_NAMES.Order,
                        newOrder,
                        newSort
                    )
                }
            });
        }
    };

    return { order, sort, sortColumn };
};
